import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Premium autosave hook for long forms.
 *
 * - Snapshots `data` to localStorage under `storageKey`, debounced by `debounceMs`.
 * - On mount, returns any persisted draft via `restored` so the parent can offer
 *   a "Resume" banner. The parent owns whether to apply it.
 * - Exposes a `status` ("idle" | "saving" | "saved") for the AutosaveIndicator.
 * - Optional `warnOnUnload`: shows a native "leave site?" prompt when dirty.
 *
 * Strictly client-side. No backend changes.
 */
export default function useAutosave({
  storageKey,
  data,
  enabled = true,
  debounceMs = 800,
  warnOnUnload = true,
  isEmpty,           // (data) => bool ; when true we treat the form as "not yet started"
}) {
  const [restored, setRestored] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | saving | saved
  const [dirty, setDirty] = useState(false);
  const firstRunRef = useRef(true);
  const saveTimerRef = useRef(null);

  // Restore on mount.
  useEffect(() => {
    if (!enabled || !storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object" && parsed.data) {
          setRestored(parsed);
        }
      }
    } catch (e) {
      // Corrupt JSON or storage disabled — degrade silently for the user
      // but log so we can spot patterns in browser dev tools.
      // eslint-disable-next-line no-console
      console.warn("[autosave] restore failed:", e?.message || e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, enabled]);

  // Debounced save when data changes.
  useEffect(() => {
    if (!enabled || !storageKey) return;
    if (firstRunRef.current) { firstRunRef.current = false; return; }
    if (typeof isEmpty === "function" && isEmpty(data)) {
      // Nothing meaningful to save yet.
      return;
    }
    setStatus("saving");
    setDirty(true);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      try {
        const payload = { data, savedAt: Date.now() };
        localStorage.setItem(storageKey, JSON.stringify(payload));
        setLastSavedAt(payload.savedAt);
        setStatus("saved");
      } catch (e) {
        // Quota exceeded / storage disabled — drop to idle so the form stays
        // usable. We don't surface this to the user (the autosave indicator
        // simply stops updating).
        // eslint-disable-next-line no-console
        console.warn("[autosave] save failed:", e?.message || e);
        setStatus("idle");
      }
    }, debounceMs);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, enabled, storageKey, debounceMs]);

  // Beforeunload guard while dirty.
  useEffect(() => {
    if (!warnOnUnload || !dirty) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [warnOnUnload, dirty]);

  const clear = useCallback(() => {
    try { localStorage.removeItem(storageKey); } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[autosave] clear failed:", e?.message || e);
    }
    setRestored(null);
    setLastSavedAt(null);
    setDirty(false);
    setStatus("idle");
  }, [storageKey]);

  const dismissRestore = useCallback(() => {
    setRestored(null);
    try { localStorage.removeItem(storageKey); } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[autosave] dismiss failed:", e?.message || e);
    }
  }, [storageKey]);

  return { restored, lastSavedAt, status, dirty, clear, dismissRestore };
}

/** Human-readable "saved X ago" for the indicator. */
export function timeAgo(ts) {
  if (!ts) return "";
  const s = Math.max(1, Math.round((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  return `${h}h ago`;
}
